import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';

import { Grid } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import PersonalizedEmbeddedMsg from 'bundles/degree-description/components/modules/personalization/PersonalizedEmbeddedMsg';
import { getCtaActionXddp, getMessagesToDisplay } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import StudentExperiencePageSchemaMarkup from 'bundles/xddp/components/seo/StudentExperiencePageSchemaMarkup';
import Container from 'bundles/xddp/components/shared/Container';
import InfoSidebar from 'bundles/xddp/components/shared/InfoSidebar';
import StudentExperienceHighlights from 'bundles/xddp/components/student-experience/StudentExperienceHighlights';
import StudentVideoStories from 'bundles/xddp/components/student-experience/StudentVideoStories';
import { useScrollToStickyBar } from 'bundles/xddp/hooks/useScrollToStickyBar';
import type { DegreeSubPageProps } from 'bundles/xddp/types';

import 'css!bundles/xddp/components/__styles__/StudentExperiencePage';

const StudentExperiencePage: React.FC<DegreeSubPageProps> = ({
  pageContent,
  userAgent,
  personalizedDdpData,
  handlePersonalizedRequestInfoClick,
  isStickyBarPresent,
}) => {
  const {
    expressionOfInterestContainer,
    slug,
    programBenefitsOverview,
    studentStoriesCollection,
    testimonialsTitle,
    studentExperienceOverview,
    studentExperienceHighlightsCollection,
    atAGlance,
  } = pageContent;

  const messagesToDisplay = getMessagesToDisplay(
    personalizedDdpData,
    slug,
    pageContent.programName,
    pageContent.partnerInformation?.partnerName
  );

  const embeddedAction = getCtaActionXddp(
    messagesToDisplay?.embeddedMessageToDisplay?.ctaActionType,
    expressionOfInterestContainer?.applyButtonUrl ?? expressionOfInterestContainer?.hostedApplicationUrl,
    handlePersonalizedRequestInfoClick
  );

  useScrollToStickyBar(isStickyBarPresent);

  return (
    <div className="rc-StudentExperiencePage">
      <StudentExperiencePageSchemaMarkup pageContent={pageContent} />
      <Container>
        <Grid container>
          <Grid item md={8} sm={12}>
            <Markdown className="programBenefitsOverview" source={programBenefitsOverview} />

            {messagesToDisplay.embeddedMessageToDisplay && (
              <PersonalizedEmbeddedMsg
                personalizedMessage={messagesToDisplay.embeddedMessageToDisplay}
                ctaAction={embeddedAction}
                onXddp
              />
            )}

            <StudentVideoStories
              studentStories={studentStoriesCollection?.items}
              testimonialsTitle={testimonialsTitle}
            />

            <StudentExperienceHighlights
              userAgent={userAgent}
              overview={studentExperienceOverview}
              highlights={studentExperienceHighlightsCollection?.items}
            />
          </Grid>
          <InfoSidebar userAgent={userAgent} atAGlance={atAGlance} />
        </Grid>
      </Container>
    </div>
  );
};

export default compose<DegreeSubPageProps, DegreeSubPageProps>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'xddp',
      page: 'student_experience_page',
    },
  }))
)(StudentExperiencePage);
